import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import useWarningsStore from '../../store/warningsStore';
import { LoadingState } from '../ui';
import {
  archiveExporterCostCategory,
  EXPORTER_COST_SPLIT_BASIS_LABELS,
  ExporterCostCategory,
  ExporterCostMappingChange,
  ExporterCostSplitBasis,
  getExporterCostCategories,
  getExporterCostMappingChanges,
  getUnclassifiedExporterCosts,
  setReceiverCostExporterCategories,
  UNCLASSIFIED_EXPORTER_COST_LABEL,
  UnclassifiedExporterCost,
  upsertExporterCostCategory,
} from 'common/src/api/exporter-cost-categories';
import { OFFICIAL_COST_CATEGORY_LABELS } from 'common/src/api/receiver-costs';
import { ReceiverCostConceptsSection } from './ReceiverCostConcepts';

type CategoryDraft = {
  id: number | null;
  name: string;
  externalCode: string;
  splitBasis: ExporterCostSplitBasis;
  displayOrder: string;
};

const emptyDraft: CategoryDraft = {
  id: null,
  name: '',
  externalCode: '',
  splitBasis: 'kg',
  displayOrder: '0',
};

const formatUsd = (value: number) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);

interface ExporterCostCategoriesSectionProps {
  /** Called whenever the unclassified list is (re)loaded. */
  onUnclassifiedCountChange?: (count: number) => void;
}

export const ExporterCostCategoriesSection = ({
  onUnclassifiedCountChange,
}: ExporterCostCategoriesSectionProps) => {
  const { t } = useTranslation('settings');
  const { userProfile } = useUserStore();
  const { setUnclassifiedExporterCostsCount } = useWarningsStore();
  const exporterId = userProfile?.exporter_id;
  const isAdmin =
    userProfile?.roles?.includes('admin') ||
    userProfile?.roles?.includes('superadmin');
  const [categories, setCategories] = useState<ExporterCostCategory[]>([]);
  const [unclassified, setUnclassified] = useState<UnclassifiedExporterCost[]>(
    []
  );
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [changes, setChanges] = useState<ExporterCostMappingChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [draft, setDraft] = useState<CategoryDraft>(emptyDraft);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const activeCategories = useMemo(
    () => categories.filter((category) => !category.archivedAt),
    [categories]
  );
  const categoryName = useCallback(
    (categoryId: number | null) =>
      categoryId === null
        ? t(UNCLASSIFIED_EXPORTER_COST_LABEL)
        : categories.find((category) => category.id === categoryId)?.name ||
          '—',
    [categories, t]
  );
  const unclassifiedKey = (cost: UnclassifiedExporterCost) =>
    `${cost.exporterReceiverId}:${cost.normalizedConcept}`;

  const loadAll = useCallback(async () => {
    if (!exporterId) return;
    setLoading(true);
    try {
      const [nextCategories, nextUnclassified, nextChanges] = await Promise.all(
        [
          getExporterCostCategories(exporterId, true),
          getUnclassifiedExporterCosts(exporterId),
          getExporterCostMappingChanges(exporterId),
        ]
      );
      setCategories(nextCategories);
      setUnclassified(nextUnclassified);
      setChanges(nextChanges);
      setAssignments(
        Object.fromEntries(
          nextUnclassified
            .filter((cost) => cost.suggestedCategoryId !== null)
            .map((cost) => [
              unclassifiedKey(cost),
              String(cost.suggestedCategoryId),
            ])
        )
      );
      onUnclassifiedCountChange?.(nextUnclassified.length);
      setUnclassifiedExporterCostsCount(nextUnclassified.length);
    } catch (error) {
      console.error('Error loading exporter cost categories:', error);
      toast.error(t('Error al cargar las categorías de costos.'));
    } finally {
      setLoading(false);
    }
  }, [
    exporterId,
    onUnclassifiedCountChange,
    setUnclassifiedExporterCostsCount,
    t,
  ]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openEditor = (category?: ExporterCostCategory) => {
    setDraft(
      category
        ? {
            id: category.id,
            name: category.name,
            externalCode: category.externalCode || '',
            splitBasis: category.splitBasis,
            displayOrder: String(category.displayOrder),
          }
        : {
            ...emptyDraft,
            displayOrder: String(activeCategories.length + 1),
          }
    );
    onOpen();
  };

  const handleSave = async () => {
    if (!exporterId || !draft.name.trim()) return;
    setSaving(true);
    try {
      await upsertExporterCostCategory(exporterId, {
        id: draft.id,
        name: draft.name,
        externalCode: draft.externalCode,
        splitBasis: draft.splitBasis,
        displayOrder: Number(draft.displayOrder) || 0,
      });
      toast.success(t('Categoría guardada.'));
      onClose();
      await loadAll();
    } catch (error: any) {
      console.error('Error saving exporter cost category:', error);
      toast.error(error?.message || t('Error al guardar la categoría.'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (category: ExporterCostCategory) => {
    try {
      await archiveExporterCostCategory(category.id);
      toast.success(t('Categoría archivada.'));
      await loadAll();
    } catch (error) {
      console.error('Error archiving exporter cost category:', error);
      toast.error(t('Error al archivar la categoría.'));
    }
  };

  const pendingAssignments = unclassified.filter(
    (cost) => assignments[unclassifiedKey(cost)]
  );

  const handleSaveAssignments = async () => {
    const byReceiver = new Map<
      number,
      Array<{ name: string; categoryId: number }>
    >();
    pendingAssignments.forEach((cost) => {
      const items = byReceiver.get(cost.exporterReceiverId) || [];
      items.push({
        name: cost.sampleOriginalName,
        categoryId: Number(assignments[unclassifiedKey(cost)]),
      });
      byReceiver.set(cost.exporterReceiverId, items);
    });

    setSavingAssignments(true);
    try {
      for (const [receiverId, items] of byReceiver) {
        await setReceiverCostExporterCategories(receiverId, items);
      }
      toast.success(
        t('{count} costos asignados.').replace(
          '{count}',
          String(pendingAssignments.length)
        )
      );
      await loadAll();
    } catch (error: any) {
      console.error('Error saving exporter cost assignments:', error);
      toast.error(error?.message || t('Error al guardar las asignaciones.'));
    } finally {
      setSavingAssignments(false);
    }
  };

  return (
    <div>
      <div className='mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
        <div>
          <p className='text-lg font-bold'>{t('Categorías de costos')}</p>
          <p className='text-sm text-default-400'>
            {t(
              'Define tus propias categorías de costos y cómo se prorratean entre las líneas de venta. Los costos del cliente se asignan a estas categorías al revisar cada liquidación.'
            )}
          </p>
          <p className='text-xs text-default-400'>
            {t(
              'Los cambios de prorrateo y de asignación se aplican a todas las liquidaciones desde la temporada 2026-2027.'
            )}
          </p>
        </div>
        {isAdmin && (
          <Button
            size='sm'
            color='primary'
            onPress={() => openEditor()}
          >
            <Icon icon='tabler:plus' />
            {t('Nueva categoría')}
          </Button>
        )}
      </div>

      {loading ? (
        <LoadingState />
      ) : (
        <Table aria-label={t('Categorías de costos')} removeWrapper>
          <TableHeader>
            <TableColumn>{t('Nombre')}</TableColumn>
            <TableColumn>{t('Código ERP')}</TableColumn>
            <TableColumn>{t('Prorrateo')}</TableColumn>
            <TableColumn>{t('Orden')}</TableColumn>
            <TableColumn align='end'>{t('Acciones')}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={t('Aún no hay categorías de costos.')}>
            {activeCategories.map((category) => (
              <TableRow key={category.id}>
                <TableCell>{category.name}</TableCell>
                <TableCell>{category.externalCode || '—'}</TableCell>
                <TableCell>
                  {t(EXPORTER_COST_SPLIT_BASIS_LABELS[category.splitBasis])}
                </TableCell>
                <TableCell>{category.displayOrder}</TableCell>
                <TableCell>
                  {isAdmin && (
                    <div className='flex justify-end gap-1'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        aria-label={t('Editar')}
                        onPress={() => openEditor(category)}
                      >
                        <Icon icon='tabler:edit' />
                      </Button>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color='danger'
                        aria-label={t('Archivar')}
                        onPress={() => handleArchive(category)}
                      >
                        <Icon icon='tabler:trash' />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Divider className='my-6' />

      <div className='mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
        <div>
          <p className='text-lg font-bold'>
            {t('Costos sin clasificar')}{' '}
            <Chip
              size='sm'
              variant='flat'
              color={unclassified.length > 0 ? 'warning' : 'success'}
            >
              {unclassified.length}
            </Chip>
          </p>
          <p className='text-sm text-default-400'>
            {t(
              'Conceptos de costos de tus clientes (desde 2026-2027) que aún no tienen una de tus categorías asignada. Mientras no los asignes, se muestran como "Sin clasificar".'
            )}
          </p>
        </div>
        <Button
          size='sm'
          color='primary'
          isLoading={savingAssignments}
          isDisabled={pendingAssignments.length === 0}
          onPress={handleSaveAssignments}
        >
          {t('Guardar {count} asignaciones').replace(
            '{count}',
            String(pendingAssignments.length)
          )}
        </Button>
      </div>

      {!loading && (
        <Table aria-label={t('Costos sin clasificar')} removeWrapper>
          <TableHeader>
            <TableColumn>{t('Cliente')}</TableColumn>
            <TableColumn>{t('Concepto')}</TableColumn>
            <TableColumn>{t('Costo Estándar FruitMarkets')}</TableColumn>
            <TableColumn>{t('Liquidaciones')}</TableColumn>
            <TableColumn>{t('Total USD')}</TableColumn>
            <TableColumn>{t('Asignar a')}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={t('No hay costos sin clasificar.')}>
            {unclassified.map((cost) => {
              const key = unclassifiedKey(cost);
              return (
                <TableRow key={key}>
                  <TableCell>{cost.receiverName}</TableCell>
                  <TableCell>{cost.sampleOriginalName}</TableCell>
                  <TableCell>
                    {cost.officialCategory
                      ? t(OFFICIAL_COST_CATEGORY_LABELS[cost.officialCategory])
                      : '—'}
                  </TableCell>
                  <TableCell>{cost.settlementCount}</TableCell>
                  <TableCell>{formatUsd(cost.totalUsd)}</TableCell>
                  <TableCell>
                    <div className='flex min-w-56 flex-col gap-1'>
                      <Select
                        size='sm'
                        aria-label={`${t('Asignar a')} ${cost.sampleOriginalName}`}
                        placeholder={t('Elegir categoría')}
                        selectedKeys={
                          assignments[key] ? [assignments[key]] : []
                        }
                        onSelectionChange={(keys) => {
                          const value = Array.from(keys)[0];
                          setAssignments((current) => {
                            const next = { ...current };
                            if (typeof value === 'string') next[key] = value;
                            else delete next[key];
                            return next;
                          });
                        }}
                      >
                        {activeCategories.map((category) => (
                          <SelectItem
                            key={String(category.id)}
                            value={String(category.id)}
                          >
                            {category.name}
                          </SelectItem>
                        ))}
                      </Select>
                      {cost.suggestionSource && (
                        <span className='text-xs text-default-400'>
                          {cost.suggestionSource === 'other_receiver'
                            ? t('Sugerido: usado con otro cliente')
                            : t('Sugerido')}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Divider className='my-6' />

      {exporterId && (
        <ReceiverCostConceptsSection
          exporterId={exporterId}
          categories={categories}
          onChanged={loadAll}
        />
      )}

      <Divider className='my-6' />

      <p className='mb-3 text-lg font-bold'>{t('Historial de asignaciones')}</p>
      {!loading && (
        <Table
          aria-label={t('Historial de asignaciones')}
          removeWrapper
          isCompact
        >
          <TableHeader>
            <TableColumn>{t('Fecha')}</TableColumn>
            <TableColumn>{t('Cliente')}</TableColumn>
            <TableColumn>{t('Concepto')}</TableColumn>
            <TableColumn>{t('Cambio')}</TableColumn>
            <TableColumn>{t('Usuario')}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={t('Aún no hay cambios.')}>
            {changes.map((change) => (
              <TableRow key={change.id}>
                <TableCell>
                  {new Date(change.changedAt).toLocaleString('es-CL')}
                </TableCell>
                <TableCell>{change.receiverName}</TableCell>
                <TableCell>{change.sampleOriginalName}</TableCell>
                <TableCell>
                  {categoryName(change.oldCategoryId)} →{' '}
                  {categoryName(change.newCategoryId)}
                </TableCell>
                <TableCell>{change.changedByName || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader>
            {draft.id ? t('Editar categoría') : t('Nueva categoría')}
          </ModalHeader>
          <ModalBody>
            <Input
              label={t('Nombre')}
              value={draft.name}
              onValueChange={(name) => setDraft({ ...draft, name })}
              isRequired
            />
            <Input
              label={t('Código ERP')}
              value={draft.externalCode}
              onValueChange={(externalCode) =>
                setDraft({ ...draft, externalCode })
              }
            />
            <Select
              label={t('Prorrateo')}
              selectedKeys={[draft.splitBasis]}
              onSelectionChange={(keys) => {
                const splitBasis = Array.from(keys)[0];
                if (typeof splitBasis !== 'string') return;
                setDraft({
                  ...draft,
                  splitBasis: splitBasis as ExporterCostSplitBasis,
                });
              }}
            >
              {(
                Object.keys(
                  EXPORTER_COST_SPLIT_BASIS_LABELS
                ) as ExporterCostSplitBasis[]
              ).map((basis) => (
                <SelectItem key={basis} value={basis}>
                  {t(EXPORTER_COST_SPLIT_BASIS_LABELS[basis])}
                </SelectItem>
              ))}
            </Select>
            <Input
              type='number'
              label={t('Orden')}
              value={draft.displayOrder}
              onValueChange={(displayOrder) =>
                setDraft({ ...draft, displayOrder })
              }
            />
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={onClose}>
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              isLoading={saving}
              isDisabled={!draft.name.trim()}
              onPress={handleSave}
            >
              {t('Guardar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
